import React, { useEffect, useRef, useState } from "react";

const baseDataSets = [
  {
    label: "Data A",
    data: { Apples: 5, Bananas: 3, Oranges: 2 },
    colors: ["#e74c3c", "#f1c40f", "#e67e22"],
  },
  {
    label: "Data B",
    data: { Red: 4, Blue: 6, Green: 2 },
    colors: ["#c0392b", "#2980b9", "#27ae60"],
  },
  {
    label: "Data C",
    data: { Dogs: 7, Cats: 1, Birds: 2 },
    colors: ["#8e44ad", "#f39c12", "#3498db"],
  },
];

// Shuffle helper
function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Generate random counts for each dataset
function generateRandomDataSets() {
  return baseDataSets.map((set) => {
    const newData = {};
    Object.keys(set.data).forEach((key) => {
      newData[key] = Math.floor(Math.random() * 10) + 1; // random 1-10
    });
    return {
      ...set,
      data: newData,
    };
  });
}

// Draw pie chart on canvas
function drawPieChart(canvas, labels, values, colors) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const total = values.reduce((a, b) => a + b, 0);
  let startAngle = -0.5 * Math.PI;
  const radius = 100;
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  labels.forEach((label, i) => {
    const sliceAngle = (values[i] / total) * 2 * Math.PI;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, startAngle, startAngle + sliceAngle);
    ctx.closePath();
    ctx.fillStyle = colors[i];
    ctx.fill();
    startAngle += sliceAngle;
  });
}

export default function MatchPieChart() {
  const [shuffledCharts, setShuffledCharts] = useState([]);
  const [shuffledTables, setShuffledTables] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const canvasRefs = useRef([]);

  function resetGame() {
    const randomizedData = generateRandomDataSets();
    setShuffledCharts(shuffle(randomizedData));
    setShuffledTables(shuffle(randomizedData));
    setAnswers({});
    setResult(null);
  }

  useEffect(() => {
    resetGame();
  }, []);

  useEffect(() => {
    if (shuffledCharts.length === 0) return;
    shuffledCharts.forEach((dataset, i) => {
      const canvas = canvasRefs.current[i];
      if (canvas) {
        drawPieChart(
          canvas,
          Object.keys(dataset.data),
          Object.values(dataset.data),
          dataset.colors
        );
      }
    });
  }, [shuffledCharts]);

  function handleSelectChange(index, value) {
    setAnswers((prev) => ({ ...prev, [index]: value }));
  }

  function checkAnswers() {
    let correctCount = 0;
    shuffledCharts.forEach((chart, i) => {
      if (answers[i] === chart.label) correctCount++;
    });

    const total = shuffledCharts.length;
    setResult(`You got ${correctCount} out of ${total} correct.`);

    if (correctCount === total) {
      setTimeout(() => {
        resetGame();
      }, 1000);
    }
  }

  return (
    <div style={{ fontFamily: "Arial, sans-serif", margin: 20, textAlign: "center" }}>
      <h1 style={{ color: "#2c3e50" }}>Match the Pie Chart</h1>
      <p>Can you match each pie chart to the correct data table?</p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 40,
          marginTop: 30,
        }}
      >
        {shuffledCharts.map((dataset, i) => (
          <div
            key={dataset.label + i}
            style={{
              border: "2px solid #ccc",
              borderRadius: 10,
              padding: 10,
              width: 300,
              position: "relative",
            }}
          >
            <canvas
              ref={(el) => (canvasRefs.current[i] = el)}
              width={220}
              height={220}
              style={{ width: "100%", height: "auto" }}
            />
            <select
              value={answers[i] || ""}
              onChange={(e) => handleSelectChange(i, e.target.value)}
              style={{ marginTop: 10, padding: 4, fontSize: "1em", width: "100%" }}
            >
              <option value="">Select matching table</option>
              {shuffledTables.map((d) => (
                <option key={d.label} value={d.label}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <hr style={{ margin: "40px 0" }} />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 40,
        }}
      >
        {shuffledTables.map((dataset) => (
          <div
            key={dataset.label}
            style={{
              border: "2px solid #ccc",
              borderRadius: 10,
              padding: 10,
              width: 300,
              position: "relative",
            }}
          >
            <h3>{dataset.label}</h3>
            <table
              style={{
                margin: "0 auto",
                borderCollapse: "collapse",
                width: "90%",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "4px 8px", border: "1px solid #aaa" }}>
                    Category
                  </th>
                  <th style={{ padding: "4px 8px", border: "1px solid #aaa" }}>
                    Count
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(dataset.data).map(([key, val]) => (
                  <tr key={key}>
                    <td style={{ padding: "4px 8px", border: "1px solid #aaa" }}>
                      {key}
                    </td>
                    <td style={{ padding: "4px 8px", border: "1px solid #aaa" }}>
                      {val}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <button
        onClick={checkAnswers}
        style={{
          marginTop: 20,
          padding: "10px 16px",
          fontSize: "1em",
          backgroundColor: "#27ae60",
          color: "white",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        Check Answers
      </button>
      <div style={{ marginTop: 20, fontSize: "1.2em" }}>{result}</div>
    </div>
  );
}
